import { useState } from 'react';
import { useMount } from 'react-use';
import { useTranslation } from 'react-i18next';
import { ITextProps } from '@honeybook/hbui/dist/components/HBText/HBText';
import { useWindowSize } from '@honeybook/hbui/dist/components/common/useWindowSize';
import { NAMESPACES } from '~/i18n/namespaces';
import useAnalytics from '~/common/useAnalytics';
import {
    GALLERY_TEMPLATE,
    MEMBER_TEMPLATE_SHARING,
    PRICED_TEMPLATE_OBJECT_TYPE,
    FREE_TEMPLATE_OBJECT_TYPE
} from '~/components/Templates/types/templates.types';
import { sendPixelEvent } from '~/utils/pixel.utils';
import { CXFlow } from '~/components/ClientExperience/OneFlow/models/cx-flow.model';
import { PUBLIC_TEMPLATE_UTM_SOURCE } from '~/components/ClientExperience/OneFlow/components/FlowView/PublishedTemplateViewHeader';
import { useGlobalCX } from '~/components/ClientExperience/providers/global-context/useGlobalCX';
import { usePostMessageToParent } from '~/components/ClientExperience/PublicTemplateIframeEvents/usePostMessageToParent';
import { isRenderedInIframe } from '~/components/ClientExperience/PublicTemplateIframeEvents/utils';
import { getSignupPublicTemplateUrl } from '~/utils/flow.utils';
import { useCXRootStore } from '~/components/ClientExperience/stores/cx-root-store.provider';

export const usePublishedTemplateViewHeader = ({ flow }: { flow: CXFlow }) => {
    const { configStore } = useGlobalCX();
    const { track } = useAnalytics();
    const { t } = useTranslation(NAMESPACES.CLIENT_EXPERIENCE, {
        keyPrefix: 'public_template'
    });
    const [isInternationalModalOpen, setIsInternationalModalOpen] =
        useState(false);
    const { isMediumUp } = useWindowSize();
    const { postMessageToParent } = usePostMessageToParent();

    const {
        flowViewUiStore: { isInIframePreview }
    } = useCXRootStore();

    const {
        referral_details,
        offer_referral,
        is_gallery_template,
        selled_by_lead_form_id
    } = flow?.public_configuration || {};

    useMount(() => {
        const stableId = localStorage.getItem(
            'STATSIG_LOCAL_STORAGE_STABLE_ID'
        );
        track('template public preview', {
            action: 'load',
            template_id: flow._id,
            template_name: flow.title,
            recipient_test_variant: isInIframePreview ? 'recipient' : 'control',
            source:
                is_gallery_template || !!selled_by_lead_form_id
                    ? GALLERY_TEMPLATE
                    : MEMBER_TEMPLATE_SHARING,
            object_type: !!selled_by_lead_form_id
                ? PRICED_TEMPLATE_OBJECT_TYPE
                : FREE_TEMPLATE_OBJECT_TYPE
        });
        sendPixelEvent({
            action: 'public_template_preview',
            stable_id: stableId,
            template_id: flow._id,
            is_gallery_template:
                is_gallery_template || !!selled_by_lead_form_id,
            object_type: !!selled_by_lead_form_id
                ? PRICED_TEMPLATE_OBJECT_TYPE
                : FREE_TEMPLATE_OBJECT_TYPE
        });
    });

    const buttonLabel = t('get_template');
    const buttonTestIdName = 'get-template';

    const shouldShowReferralDiscount =
        !!offer_referral &&
        !!referral_details &&
        !!referral_details.discount_amount &&
        !!referral_details.discount_duration;

    const clickUseTemplate = () => {
        const signupUrl = getSignupPublicTemplateUrl({
            flow,
            configStore,
            publicTemplateUtmSource: PUBLIC_TEMPLATE_UTM_SOURCE
        });

        if (isRenderedInIframe) {
            postMessageToParent({
                event: 'click_use_template',
                data: { signupUrl }
            });
        } else {
            window.location.href = signupUrl;
        }
    };

    const createdInProps: ITextProps = {
        type: isMediumUp ? 'emphasis12' : 'caption12',
        color: isMediumUp ? 'neutral400' : 'neutral500'
    };

    return {
        buttonLabel,
        buttonTestIdName,
        clickUseTemplate,
        isInternationalModalOpen,
        setIsInternationalModalOpen,
        createdInProps,
        shouldShowReferralDiscount
    };
};
